import { PiggyBankIcon, PlusIcon, UsersThreeIcon } from '@phosphor-icons/react'
import { applyPercent, formatMoney } from '@paybae/shared'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { EmptyState } from '@/components/Card'
import { Donut, type DonutSegment, chartColor } from '@/components/Donut'
import { ErrorState, Skeleton } from '@/components/Estado'
import { AvisoError, FormSheet, Guardar } from '@/components/Form'
import {
  ApiError,
  type AllocationItem,
  type AllocationPlan,
  type IncomeSource,
  type PayFrequency,
} from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import { useGuardarReparto, useIngresos, useReparto } from '@/lib/queries'
import { useSession } from '@/lib/session'

/**
 * Reparto del ingreso entre fondos (§5, §7).
 *
 * Responde una sola pregunta, y por eso la dona va arriba del todo: **¿cuánto de
 * lo que gano ya tiene destino?** Los porcentajes sueltos no la contestan —«15%
 * al viaje, 10% a la emergencia» obliga a sumar de cabeza—, así que el reparto
 * se dibuja como un todo cerrado y se mueve mientras el dedo arrastra. Guardar
 * y luego descubrir que uno se comprometió con el 80% del sueldo llega tarde.
 *
 * El reparto es **de cada integrante**. Un fondo del hogar aparece en el plan de
 * los dos y cada quien pone su propio porcentaje: el servidor guarda una fila
 * por persona y fondo, así que mover este control nunca toca el de la pareja.
 *
 * El guardado sustituye el plan entero —así funciona el `PUT`—, por eso se manda
 * completo y no solo lo que se movió.
 */

/** Pasos de cinco: nadie decide destinar el 37% de su sueldo a un viaje. */
const PASO = 5

/**
 * El tramo sin destinar no toma color de la rampa de gráficas: no es un fondo
 * más, es la ausencia de uno. El gris de tinta tenue lo dice sin competir con
 * los fondos, y además va siempre etiquetado abajo con su cifra — el color no es
 * lo único que lo identifica.
 */
const COLOR_LIBRE = 'var(--ink-faint)'

const SIN_REPARTIR = 'sin-repartir'

export function Reparto() {
  const navigate = useNavigate()
  const { membership } = useSession()
  // Sin `memberId` el servidor devuelve el reparto de quien pregunta, que es
  // también de quien se leen los ingresos: las dos consultas van en paralelo.
  const plan = useReparto()
  const fuentes = useIngresos(membership.id)

  const cerrar = (): void => {
    void navigate('/ahorro')
  }

  // Se espera a las dos: sin los ingresos no hay con qué traducir los
  // porcentajes a pesos, y un reparto en porcentajes pelados es justo lo que
  // esta pantalla existe para evitar.
  if (plan.isPending || fuentes.isPending) return <HojaCargando onCerrar={cerrar} />

  const fallo = plan.error ?? fuentes.error
  if (fallo || !plan.data || !fuentes.data) {
    return (
      <HojaDeAviso onCerrar={cerrar}>
        <ErrorState
          titulo="No pudimos cargar tu reparto"
          mensaje={fallo instanceof Error ? fallo.message : 'Inténtalo de nuevo.'}
          onReintentar={() => {
            void plan.refetch()
            void fuentes.refetch()
          }}
        />
      </HojaDeAviso>
    )
  }

  return (
    <FormularioReparto
      plan={plan.data}
      ingresoCents={ingresoMensualDe(fuentes.data)}
      onCerrar={cerrar}
    />
  )
}

function FormularioReparto({
  plan,
  ingresoCents,
  onCerrar,
}: {
  plan: AllocationPlan
  /** Lo que gana al mes (ver `ingresoMensualDe`). `0` sin ingreso fijo registrado. */
  ingresoCents: number
  onCerrar: () => void
}) {
  const guardar = useGuardarReparto()
  const navigate = useNavigate()

  // El estado arranca del plan del servidor y ya no se vuelve a sincronizar: si
  // una consulta de fondo refrescara el plan a media edición, los controles
  // saltarían bajo el dedo.
  const [porcentajes, setPorcentajes] = useState<Record<string, number>>(() =>
    Object.fromEntries(plan.items.map((item) => [item.goalId, item.percent ?? 0])),
  )

  const total = plan.items.reduce((suma, item) => suma + (porcentajes[item.goalId] ?? 0), 0)
  const libre = Math.max(0, 100 - total)
  const sobran = Math.max(0, total - 100)

  const cambiado = plan.items.some(
    (item) => (porcentajes[item.goalId] ?? 0) !== (item.percent ?? 0),
  )

  const segmentos: DonutSegment[] = useMemo(() => {
    const deFondos = plan.items
      .map((item, i) => ({
        key: item.goalId,
        label: item.name,
        value: porcentajes[item.goalId] ?? 0,
        color: chartColor(i),
      }))
      // La dona ya descarta los ceros, pero filtrarlos aquí mantiene el orden de
      // colores estable: un fondo en cero no debe correr el color de los demás.
      .filter((s) => s.value > 0)

    return [
      ...deFondos,
      { key: SIN_REPARTIR, label: 'Sin repartir', value: libre, color: COLOR_LIBRE },
    ]
  }, [plan.items, porcentajes, libre])

  const ponerPorcentaje = (goalId: string, valor: number): void => {
    setPorcentajes((previo) => (previo[goalId] === valor ? previo : { ...previo, [goalId]: valor }))
  }

  const enviar = (): void => {
    if (sobran > 0 || !cambiado || guardar.isPending) return

    // Viaja el plan completo, incluidos los ceros que el servidor traducirá a
    // «no participa». Mandar solo lo que cambió dejaría vivos los porcentajes
    // que el usuario acaba de bajar a cero.
    guardar.mutate(
      plan.items.map((item) => ({ goalId: item.goalId, percent: porcentajes[item.goalId] ?? 0 })),
      {
        onSuccess: () => {
          haptic('success')
          onCerrar()
        },
        onError: () => haptic('error'),
      },
    )
  }

  if (plan.items.length === 0) {
    return (
      <HojaDeAviso onCerrar={onCerrar}>
        <EmptyState
          icon={<PiggyBankIcon size={32} weight="fill" />}
          title="Todavía no tienes fondos que repartir"
          description="Crea primero un fondo —una meta, tu reserva de emergencia o una bolsa general— y aquí decides qué parte de cada ingreso se va a cada uno."
          action={
            <button
              type="button"
              onClick={() => {
                haptic('light')
                // Se reemplaza la pantalla en vez de apilarla: el alta vuelve a
                // Ahorro al guardar, y desde ahí se entra al reparto ya con algo
                // que repartir. Apilarla dejaría este reparto vacío debajo.
                void navigate('/ahorro/nueva', { replace: true })
              }}
              className="flex min-h-[44px] items-center gap-2 rounded-pill bg-accion px-5 text-[14px] font-semibold text-on-accion active:scale-95"
            >
              <PlusIcon size={16} weight="bold" />
              Crear mi primer fondo
            </button>
          }
        />
      </HojaDeAviso>
    )
  }

  return (
    <FormSheet
      tinte="mint"
      titulo="Reparto de tu ingreso"
      onCerrar={onCerrar}
      accion={
        <div className="flex flex-col gap-2.5">
          {sobran > 0 ? (
            <AvisoError
              mensaje={`Te pasaste por ${sobran} ${sobran === 1 ? 'punto' : 'puntos'}. Bájalos entre los fondos para poder guardar.`}
            />
          ) : (
            <AvisoError mensaje={guardar.error ? mensajeDeError(guardar.error) : null} />
          )}
          <Guardar
            onClick={enviar}
            disabled={sobran > 0 || !cambiado}
            guardando={guardar.isPending}
          >
            {sobran > 0
              ? `Baja ${sobran} ${sobran === 1 ? 'punto' : 'puntos'}`
              : cambiado
                ? 'Guardar reparto'
                : 'Sin cambios que guardar'}
          </Guardar>
        </div>
      }
    >
      <Resumen
        segmentos={segmentos}
        total={total}
        libre={libre}
        sobran={sobran}
        ingresoCents={ingresoCents}
      />

      <div className="flex flex-col gap-3">
        {plan.items.map((item, i) => (
          <FilaFondo
            key={item.goalId}
            item={item}
            color={chartColor(i)}
            percent={porcentajes[item.goalId] ?? 0}
            ingresoCents={ingresoCents}
            onChange={(valor) => ponerPorcentaje(item.goalId, valor)}
          />
        ))}
      </div>

      {plan.items.some((item) => item.ownerMemberId === null) ? (
        <p className="flex items-start gap-2 rounded-card bg-surface-sunken px-3.5 py-3 text-[12px] leading-snug text-ink-soft">
          <UsersThreeIcon size={15} weight="fill" className="mt-0.5 shrink-0" />
          En los fondos del hogar, el porcentaje que pones aquí sale de tu ingreso. Cada quien
          decide el suyo por separado.
        </p>
      ) : null}

      {ingresoCents <= 0 ? (
        // Sin nómina registrada los porcentajes no se pueden traducir a pesos.
        // Decirlo, en vez de enseñar $0 en cada fila, que se leería como un dato.
        <p className="rounded-card bg-surface-sunken px-3.5 py-3 text-[12px] leading-snug text-ink-soft">
          Para ver cuánto es cada porcentaje en pesos, registra tu ingreso en Ajustes → Ingresos.
        </p>
      ) : null}
    </FormSheet>
  )
}

/**
 * La dona y el contador de lo libre.
 *
 * En el centro van los pesos y no el porcentaje: «$4,200 al mes» es una cifra
 * con la que se puede decidir algo, «15%» todavía hay que traducirlo. Cuando no
 * hay ingreso registrado no queda más remedio que enseñar el porcentaje.
 */
function Resumen({
  segmentos,
  total,
  libre,
  sobran,
  ingresoCents,
}: {
  segmentos: readonly DonutSegment[]
  total: number
  libre: number
  sobran: number
  ingresoCents: number
}) {
  const hayIngreso = ingresoCents > 0
  // Sin tope: pasarse de 100 tiene que verse en pesos. «Estás comprometiendo
  // $29,242 de los $27,850 que ganas» dice el problema mejor que cualquier
  // aviso, y recortar la cifra a 100% lo escondería justo cuando importa.
  const comprometidoCents = applyPercent(ingresoCents, total)
  const libreCents = applyPercent(ingresoCents, libre)

  return (
    <section className="flex flex-col items-center gap-3 rounded-card border border-line bg-surface p-4">
      <Donut
        segments={segmentos}
        centerValue={
          hayIngreso ? formatMoney(comprometidoCents, { hideZeroCents: true }) : `${total}%`
        }
        centerLabel={hayIngreso ? `${total}% de tu ingreso` : 'de tu ingreso con destino'}
      />

      {/* El contador de lo libre en tinta, nunca en verde: aquí no hay nada
          bueno ni malo que anunciar —repartir mucho es ahorrar mucho—. El rojo
          se reserva para el único caso que sí exige corregir algo: pasarse. */}
      {sobran > 0 ? (
        <p className="text-center text-[13px] font-semibold text-expense-text">
          Estás repartiendo el {total}% de lo que ganas.
        </p>
      ) : (
        <p className="text-center text-[13px] leading-snug text-ink-soft">
          Te quedan libres{' '}
          <strong data-money className="font-semibold text-ink">
            {libre}%
          </strong>
          {hayIngreso ? (
            <>
              {' · '}
              <strong data-money className="font-semibold text-ink">
                {formatMoney(libreCents, { hideZeroCents: true })}
              </strong>{' '}
              al mes
            </>
          ) : null}
        </p>
      )}
    </section>
  )
}

function FilaFondo({
  item,
  color,
  percent,
  ingresoCents,
  onChange,
}: {
  item: AllocationItem
  color: string
  percent: number
  ingresoCents: number
  onChange: (valor: number) => void
}) {
  const delHogar = item.ownerMemberId === null
  const pesosCents = applyPercent(ingresoCents, percent)

  return (
    <section className="flex flex-col gap-1 rounded-card border border-line bg-surface p-3.5">
      <header className="flex items-start gap-2.5">
        <span
          aria-hidden="true"
          className="mt-[7px] size-2.5 shrink-0 rounded-full"
          style={{ background: color }}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-semibold text-ink">{item.name}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
            {delHogar ? (
              <span className="inline-flex items-center gap-1 rounded-pill bg-surface-sunken px-2 py-0.5 text-[11px] font-medium text-ink-soft">
                <UsersThreeIcon size={11} weight="fill" />
                del hogar
              </span>
            ) : null}
            <span className="text-[12px] text-ink-faint">{contextoDe(item)}</span>
          </p>
        </div>
        <span data-money className="shrink-0 font-mono text-[17px] font-bold text-ink">
          {percent}%
        </span>
      </header>

      <Deslizador
        etiqueta={`Porcentaje de tu ingreso para ${item.name}`}
        valor={percent}
        color={color}
        onChange={onChange}
      />

      {/* La traducción a pesos va debajo del control, no arriba: es la
          consecuencia de moverlo, y leerla justo donde acaba el dedo es lo que
          convierte «15%» en una decisión informada. */}
      {ingresoCents > 0 ? (
        <p className="text-[12px] leading-snug text-ink-soft">
          <strong data-money className="font-semibold text-ink">
            {formatMoney(pesosCents, { hideZeroCents: true })}
          </strong>{' '}
          al mes
        </p>
      ) : null}
    </section>
  )
}

/** Diámetro del pulgar. Define también el recorrido útil de la pista. */
const PULGAR = 28

/**
 * Control de porcentaje.
 *
 * La pista y el pulgar se dibujan aparte, y encima va un `<input type="range">`
 * de verdad al que solo se le quita la pintura. Es lo que permite dar 44 px de
 * alto real y colorear el control con las fichas del tema sin pelearse con los
 * pseudoelementos de cada navegador, que cambian de nombre entre WebKit y Gecko
 * y no aceptan un color distinto por elemento.
 *
 * Lo transparente es la pintura, no el control: el `input` conserva su tamaño y
 * su papel, así que el teclado, el lector de pantalla y el salto al tocar la
 * pista siguen funcionando sin reimplementar nada. Ponerle `opacity-0` habría
 * sido más corto, pero deja el control fuera del alcance de varias herramientas
 * de accesibilidad.
 */
function Deslizador({
  etiqueta,
  valor,
  color,
  onChange,
}: {
  etiqueta: string
  valor: number
  color: string
  onChange: (valor: number) => void
}) {
  return (
    <div className="relative flex h-11 w-full items-center rounded-pill has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-ink">
      <span aria-hidden="true" className="absolute inset-x-0 h-2 rounded-pill bg-surface-sunken" />
      <span
        aria-hidden="true"
        className="absolute left-0 h-2 rounded-pill"
        style={{ width: `${valor}%`, background: color }}
      />
      {/* El pulgar se mueve dentro del recorrido útil —medio pulgar de margen a
          cada lado—, igual que el del control nativo que lleva debajo: así lo
          que se ve y lo que se arrastra coinciden, y en 0% y 100% no sobresale
          de la tarjeta. */}
      <span
        aria-hidden="true"
        className="absolute -translate-x-1/2 rounded-full border-2 border-surface shadow-sm"
        style={{
          width: PULGAR,
          height: PULGAR,
          left: `calc(${PULGAR / 2}px + (100% - ${PULGAR}px) * ${valor / 100})`,
          background: color,
        }}
      />
      <input
        type="range"
        min={0}
        max={100}
        step={PASO}
        value={valor}
        aria-label={etiqueta}
        aria-valuetext={`${valor} por ciento`}
        onChange={(e) => {
          const siguiente = Number(e.target.value)
          if (siguiente !== valor) haptic('selection')
          onChange(siguiente)
        }}
        className={cn(
          'absolute inset-0 size-full cursor-pointer appearance-none bg-transparent focus-visible:outline-none',
          // El pulgar nativo conserva su ancho —de él depende el recorrido— y
          // solo pierde el relleno; el dibujado encima ocupa su lugar.
          '[&::-webkit-slider-runnable-track]:h-11 [&::-webkit-slider-runnable-track]:bg-transparent',
          '[&::-webkit-slider-thumb]:size-7 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-transparent',
          '[&::-moz-range-track]:h-11 [&::-moz-range-track]:bg-transparent',
          '[&::-moz-range-thumb]:size-7 [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-transparent',
        )}
      />
    </div>
  )
}

/** Cuántas veces cae al mes cada frecuencia de pago, en promedio. */
const VECES_AL_MES: Record<Exclude<PayFrequency, 'IRREGULAR'>, number> = {
  MONTHLY: 1,
  SEMI_MONTHLY: 2,
  BIWEEKLY: 26 / 12,
  WEEKLY: 52 / 12,
}

/**
 * Lo que gana el integrante en un mes, que es lo que dice cada «al mes».
 *
 * Antes se tomaba `expectedIncomeCents` del disponible, y eso mide otra cosa:
 * lo que **falta por caer** del periodo. Bajaba en cuanto se confirmaba una
 * quincena, así que el mismo 20% valía $9,400 al mes el día 2 y $4,700 el 16,
 * sin que nadie hubiera tocado nada. El reparto es una decisión sobre el
 * sueldo, y el sueldo no cambia porque ya haya caído la mitad.
 *
 * Se suman los ingresos fijos activos por su frecuencia. Un ingreso variable
 * con calendario no entra: su monto es una estimación y prometería pesos que
 * quizá no lleguen. Uno sin calendario entra por lo que de verdad se cobró en
 * promedio, que es dato y no apuesta. Si no queda nada, cero, y la pantalla lo
 * dice en vez de inventar pesos.
 */
function ingresoMensualDe(fuentes: readonly IncomeSource[]): number {
  let total = 0
  for (const fuente of fuentes) {
    if (!fuente.active) continue
    if (fuente.frequency === 'IRREGULAR') {
      total += fuente.historial?.promedioMensualCents ?? 0
    } else if (fuente.kind === 'FIXED') {
      total += fuente.netAmountCents * VECES_AL_MES[fuente.frequency]
    }
  }
  return Math.round(total)
}

/**
 * Cómo va este fondo, en cuatro palabras.
 *
 * Una meta se mide contra su objetivo; un fondo sin final se mide por lo que
 * lleva guardado. Decirle «meta de $1,500» a una reserva de emergencia sería
 * inventarle un final que nadie le puso.
 */
function contextoDe(item: AllocationItem): string {
  if (item.kind === 'GOAL') {
    return item.targetAmountCents === null
      ? 'Meta sin monto objetivo'
      : `Meta de ${formatMoney(item.targetAmountCents, { hideZeroCents: true })}`
  }
  const nombre = item.kind === 'EMERGENCY' ? 'Reserva' : 'Bolsa'
  return `${nombre} · llevas ${formatMoney(item.currentAmountCents, { hideZeroCents: true })}`
}

function mensajeDeError(error: unknown): string {
  if (error instanceof ApiError) {
    const detalles = Object.values(error.fieldErrors ?? {}).flat()
    return detalles.length > 0 ? detalles.join('. ') : error.message
  }
  return 'No pudimos guardar el reparto. Inténtalo de nuevo.'
}

/** Mismo esqueleto que la pantalla real, para que nada salte al llegar los datos. */
function HojaCargando({ onCerrar }: { onCerrar: () => void }) {
  return (
    <FormSheet
      tinte="mint"
      titulo="Reparto de tu ingreso"
      onCerrar={onCerrar}
      accion={<Guardar onClick={() => undefined} disabled>Cargando…</Guardar>}
    >
      <div className="flex flex-col items-center gap-3 rounded-card border border-line bg-surface p-4">
        <Skeleton className="size-[120px] rounded-full" />
        <Skeleton className="h-4 w-48" />
      </div>
      <div className="flex flex-col gap-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex flex-col gap-1 rounded-card border border-line bg-surface p-3.5">
            <div className="flex h-[40px] items-start gap-2.5">
              <Skeleton className="mt-[7px] size-2.5 rounded-full" />
              <div className="flex-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="mt-1.5 h-3 w-24" />
              </div>
              <Skeleton className="h-5 w-10" />
            </div>
            <div className="flex h-11 items-center">
              <Skeleton className="h-2 w-full rounded-pill" />
            </div>
            <Skeleton className="h-4 w-24" />
          </div>
        ))}
      </div>
    </FormSheet>
  )
}

function HojaDeAviso({ onCerrar, children }: { onCerrar: () => void; children: React.ReactNode }) {
  return (
    <FormSheet
      tinte="mint"
      titulo="Reparto de tu ingreso"
      onCerrar={onCerrar}
      accion={<Guardar onClick={onCerrar}>Volver a Ahorro</Guardar>}
    >
      {children}
    </FormSheet>
  )
}
